import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CustomColorPicker from '../components/CustomColorPicker';
import Navbar from '../components/Navbar';
import api from '../services/api';
import { getCharactersForLanguage } from '../utils/alphabets';
import './MatriceEditor.css';

const SENSATION_WORD_LIMIT = 100;

const SensationModal = ({ chiffre, valeur, onSave, onClose, t, getWordCount }) => {
  const [texte, setTexte] = useState(valeur);
  const wordCount = getWordCount(texte);

  useEffect(() => {
    setTexte(valeur);
  }, [valeur]);

  const handleSave = () => {
    if (wordCount <= SENSATION_WORD_LIMIT) {
      onSave(texte);
    }
  };

  return (
    <div className="symbol-modal-overlay" onClick={onClose}>
      <div className="sensation-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('matrice.sensationFor') || 'Sensation pour'} {chiffre}</h3>
        <textarea
          className="sensation-textarea"
          value={texte}
          onChange={(e) => setTexte(e.target.value)}
          placeholder={t('matrice.sensationPlaceholder') || 'Décrivez la sensation de ce chiffre'}
          rows={5}
        />
        <div className={`sensation-word-count ${wordCount > SENSATION_WORD_LIMIT ? 'sensation-word-count-limit' : ''}`}>
          {wordCount} / {SENSATION_WORD_LIMIT} {t('document.wordCount') || 'mots'}
        </div>
        <div className="sensation-modal-actions">
          <button className="symbol-modal-close" onClick={onClose}>
            {t('common.cancel') || 'Annuler'}
          </button>
          <button
            className="sensation-save-button"
            onClick={handleSave}
            disabled={wordCount > SENSATION_WORD_LIMIT}
          >
            {t('matrice.save') || 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MatriceEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [lettresCouleurs, setLettresCouleurs] = useState({});
  const [caracteresSpeciauxCouleurs, setCaracteresSpeciauxCouleurs] = useState({});
  const [chiffresCouleurs, setChiffresCouleurs] = useState({});
  const [chiffresSymboles, setChiffresSymboles] = useState({});
  const [chiffresSensations, setChiffresSensations] = useState({});
  const [joursSemaineCouleurs, setJoursSemaineCouleurs] = useState({});
  const [moisAnneeCouleurs, setMoisAnneeCouleurs] = useState({});
  const [selectedCell, setSelectedCell] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [matriceLangue, setMatriceLangue] = useState(null);
  const [creationLanguage, setCreationLanguage] = useState(null);
  const [isEyedropperActive, setIsEyedropperActive] = useState(false);
  const [showSymbolModal, setShowSymbolModal] = useState(false);
  const [symbolModalChiffre, setSymbolModalChiffre] = useState(null);
  const [showSensationModal, setShowSensationModal] = useState(false);
  const [sensationModalChiffre, setSensationModalChiffre] = useState(null);

  // Obtenir l'alphabet et les chiffres selon la langue de création de la matrice
  const currentLanguage = creationLanguage || i18n.language;
  const characters = getCharactersForLanguage(currentLanguage);
  const lettres = characters.letters;
  const caracteresSpeciaux = characters.specialCharacters || [];
  const chiffres = characters.numbers;
  const jours = characters.days || [];
  const mois = characters.months || [];

  useEffect(() => {
    if (id) {
      loadMatrice(id);
    } else {
      // En mode création, sauvegarder la langue actuelle
      setCreationLanguage(i18n.language);
      loadLastMatrice();
    }
  }, [id]);

  const loadMatrice = async (matriceId) => {
    try {
      const response = await api.get(`/matrices/${matriceId}/`);
      if (response.data) {
        setLettresCouleurs(response.data.lettres_couleurs || {});
        setCaracteresSpeciauxCouleurs(response.data.caracteres_speciaux_couleurs || {});
        setChiffresCouleurs(response.data.chiffres_couleurs || {});
        setChiffresSymboles(response.data.chiffres_symboles || {});
        setChiffresSensations(response.data.chiffres_sensations || {});
        setJoursSemaineCouleurs(response.data.jours_semaine_couleurs || {});
        setMoisAnneeCouleurs(response.data.mois_annee_couleurs || {});
        setMatriceLangue(response.data.langue);
        setCreationLanguage(response.data.langue);
        // Changer la langue de l'interface pour correspondre à la langue de la matrice
        if (response.data.langue && response.data.langue !== i18n.language) {
          i18n.changeLanguage(response.data.langue);
        }
      }
    } catch (error) {
      console.error('Error loading matrix:', error);
      alert(t('matrice.loadError') || 'Erreur lors du chargement de la matrice');
    }
  };

  const loadLastMatrice = async () => {
    try {
      const response = await api.get('/matrices/derniere/');
      if (response.data) {
        setLettresCouleurs(response.data.lettres_couleurs || {});
        setCaracteresSpeciauxCouleurs(response.data.caracteres_speciaux_couleurs || {});
        setChiffresCouleurs(response.data.chiffres_couleurs || {});
        setChiffresSymboles(response.data.chiffres_symboles || {});
        setChiffresSensations(response.data.chiffres_sensations || {});
        setJoursSemaineCouleurs(response.data.jours_semaine_couleurs || {});
        setMoisAnneeCouleurs(response.data.mois_annee_couleurs || {});
        setMatriceLangue(response.data.langue);
      }
    } catch (error) {
      console.error('Error loading matrix:', error);
    }
  };

  const getSymbolDisplay = (symbole) => {
    if (symbole === 'male') return '♂';
    if (symbole === 'female') return '♀';
    if (symbole === 'indeterminate') return '⚧';
    return '?';
  };

  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '37, 99, 235';
  };

  const getSymbolStyle = (symbole) => {
    if (symbole === 'male') {
      return { backgroundColor: '#2563eb', color: '#ffffff', borderColor: '#1d4ed8' };
    }
    if (symbole === 'female') {
      return { backgroundColor: '#ec4899', color: '#ffffff', borderColor: '#db2777' };
    }
    if (symbole === 'indeterminate') {
      return {
        background: 'linear-gradient(135deg, #2563eb 0%, #ec4899 100%)',
        color: '#ffffff',
        borderColor: 'transparent',
      };
    }
    return {};
  };

  const handleSymbolSquareClick = (e, chiffre) => {
    e.stopPropagation();
    setSymbolModalChiffre(chiffre);
    setShowSymbolModal(true);
  };

  const handleSymbolSelect = (symbole) => {
    if (symbolModalChiffre !== null) {
      setChiffresSymboles({ ...chiffresSymboles, [symbolModalChiffre]: symbole });
      setShowSymbolModal(false);
      setSymbolModalChiffre(null);
    }
  };

  const handleSensationRectangleClick = (e, chiffre) => {
    e.stopPropagation();
    setSensationModalChiffre(chiffre);
    setShowSensationModal(true);
  };

  const handleSensationSave = (texte) => {
    if (sensationModalChiffre !== null) {
      setChiffresSensations({ ...chiffresSensations, [sensationModalChiffre]: texte });
      setShowSensationModal(false);
      setSensationModalChiffre(null);
    }
  };

  const getWordCount = (text) => {
    if (!text || !text.trim()) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleCellClick = (type, value) => {
    // Si la pipette est active, copier la couleur de la cellule cliquée
    if (isEyedropperActive) {
      let existingColor;
      if (type === 'lettre') existingColor = lettresCouleurs[value];
      else if (type === 'caractere_special') existingColor = caracteresSpeciauxCouleurs[value];
      else if (type === 'chiffre') existingColor = chiffresCouleurs[value];
      else if (type === 'jour') existingColor = joursSemaineCouleurs[value];
      else if (type === 'mois') existingColor = moisAnneeCouleurs[value];
      else existingColor = null;
      
      if (existingColor && selectedCell) {
        // Appliquer la couleur copiée à la cellule sélectionnée
        handleColorChange({ hex: existingColor });
        setIsEyedropperActive(false);
      }
      return;
    }
    
    // Sinon, sélectionner la cellule normalement
    setSelectedCell({ type, value });
    setShowColorPicker(true);
  };

  const handleColorChange = (color) => {
    const hexColor = color.hex;
    if (selectedCell) {
      if (selectedCell.type === 'lettre') {
        setLettresCouleurs({ ...lettresCouleurs, [selectedCell.value]: hexColor });
      } else if (selectedCell.type === 'caractere_special') {
        setCaracteresSpeciauxCouleurs({ ...caracteresSpeciauxCouleurs, [selectedCell.value]: hexColor });
      } else if (selectedCell.type === 'chiffre') {
        setChiffresCouleurs({ ...chiffresCouleurs, [selectedCell.value]: hexColor });
      } else if (selectedCell.type === 'jour') {
        setJoursSemaineCouleurs({ ...joursSemaineCouleurs, [selectedCell.value]: hexColor });
      } else if (selectedCell.type === 'mois') {
        setMoisAnneeCouleurs({ ...moisAnneeCouleurs, [selectedCell.value]: hexColor });
      }
    }
  };

  // Fonction pour convertir HSL en hexadécimal
  const hslToHex = (h, s, l) => {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = n => {
      const k = (n + h / 30) % 12;
      const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
      return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
  };

  // Fonction pour générer une couleur hex aléatoire
  const randomHexColor = () => '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');

  // Fonction pour remplir toutes les valeurs au hasard
  const applyRandomColors = () => {
    const newLettres = {};
    lettres.forEach((lettre) => { newLettres[lettre] = randomHexColor(); });
    const newCaracteresSpeciaux = {};
    caracteresSpeciaux.forEach((char) => { newCaracteresSpeciaux[char] = randomHexColor(); });
    const newChiffres = {};
    chiffres.forEach((chiffre) => { newChiffres[chiffre] = randomHexColor(); });
    const newJours = {};
    jours.forEach((_, i) => { newJours[String(i)] = randomHexColor(); });
    const newMois = {};
    mois.forEach((_, i) => { newMois[String(i + 1)] = randomHexColor(); });
    setLettresCouleurs(newLettres);
    setCaracteresSpeciauxCouleurs(newCaracteresSpeciaux);
    setChiffresCouleurs(newChiffres);
    setJoursSemaineCouleurs(newJours);
    setMoisAnneeCouleurs(newMois);
  };

  // Fonction pour appliquer le dégradé arc-en-ciel à toutes les lettres
  const applyBasicGradient = () => {
    const newLettresCouleurs = {};
    const totalLetters = lettres.length;
    
    // Dégradé arc-en-ciel complet: violet (270°) → bleu (240°) → cyan (180°) → vert (120°) → jaune (60°) → orange (30°) → rouge (0°/360°)
    lettres.forEach((lettre, index) => {
      // Calculer la position dans le dégradé (0 à 1)
      const position = totalLetters > 1 ? index / (totalLetters - 1) : 0;
      
      // Convertir en angle HSL: de 270° (violet) à 0° (rouge)
      let hue = 270 - position * 270;
      // Normaliser entre 0 et 360
      if (hue < 0) hue += 360;
      if (hue >= 360) hue -= 360;
      
      // Saturation et luminosité pour des couleurs vives
      const saturation = 100;
      const lightness = 50;
      
      newLettresCouleurs[lettre] = hslToHex(hue, saturation, lightness);
    });
    
    setLettresCouleurs(newLettresCouleurs);
  };

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      const data = {
        lettres_couleurs: lettresCouleurs,
        caracteres_speciaux_couleurs: caracteresSpeciauxCouleurs,
        chiffres_couleurs: chiffresCouleurs,
        chiffres_symboles: chiffresSymboles,
        chiffres_sensations: chiffresSensations,
        jours_semaine_couleurs: joursSemaineCouleurs,
        mois_annee_couleurs: moisAnneeCouleurs,
        langue: creationLanguage || i18n.language,
      };

      if (id) {
        // Modification d'une matrice existante
        await api.put(`/matrices/${id}/`, data);
      } else {
        // Création d'une nouvelle matrice
        await api.post('/matrices/', data);
      }
      
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        if (id) {
          // Rediriger vers l'historique après modification
          navigate('/history');
        }
      }, 1500);
    } catch (error) {
      const errorDetails = {
        isUpdate: !!id,
        errorMessage: error?.message,
        errorResponse: error?.response?.data,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        hasResponse: !!error?.response,
        requestUrl: error?.config?.url,
        requestMethod: error?.config?.method,
      };
      
      console.error('Error saving matrix:', error);
      console.error('Error details:', errorDetails);
      console.error('Full error response:', error?.response);
      // Show more detailed error message
      const errorMsg = error?.response?.data?.error_message || error?.response?.data?.error || error?.message || (t('matrice.saveError') || 'Erreur lors de la sauvegarde');
      alert(`Erreur: ${errorMsg}\nStatus: ${error?.response?.status || 'N/A'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="matrice-editor">
      <Navbar disableLanguageSelector={true} />
      
      <div className="editor-content">
        <div className="editor-actions">
          {!id && (
            <button onClick={loadLastMatrice} className="load-button">
              {t('matrice.loadLast')}
            </button>
          )}
          {id && (
            <button onClick={() => navigate('/history')} className="back-button">
              {t('matrice.back') || '← Retour'}
            </button>
          )}
          <button onClick={handleSave} disabled={loading} className="save-button">
            {loading ? (id ? t('matrice.updating') || 'Mise à jour...' : 'Enregistrement...') : (id ? t('matrice.update') || 'Mettre à jour' : t('matrice.save'))}
          </button>
          {saved && <span className="saved-message">✓ {id ? (t('matrice.updated') || 'Mis à jour!') : 'Enregistré!'}</span>}
        </div>
        <div className="color-picker-container">
          {showColorPicker && selectedCell && (
            <div className="color-picker-wrapper">
              <CustomColorPicker
                color={
                  selectedCell.type === 'lettre' ? lettresCouleurs[selectedCell.value] || '#000000'
                  : selectedCell.type === 'caractere_special' ? caracteresSpeciauxCouleurs[selectedCell.value] || '#000000'
                  : selectedCell.type === 'chiffre' ? chiffresCouleurs[selectedCell.value] || '#000000'
                  : selectedCell.type === 'jour' ? joursSemaineCouleurs[selectedCell.value] || '#000000'
                  : selectedCell.type === 'mois' ? moisAnneeCouleurs[selectedCell.value] || '#000000'
                  : '#000000'
                }
                onChange={handleColorChange}
                onColorSelect={(color) => {
                  handleColorChange(color);
                  setShowColorPicker(false);
                }}
                onClose={() => setShowColorPicker(false)}
                onEyedropperActive={setIsEyedropperActive}
              />
            </div>
          )}
        </div>

        <div className="matrice-section">
          <div className="matrice-section-header">
            <h2>{t('matrice.letters')} ({characters.languageName})</h2>
            <div className="matrice-section-buttons">
              <button 
                onClick={applyRandomColors} 
                className="gradient-button"
                title={t('matrice.random')}
              >
                {t('matrice.random')}
              </button>
              <button 
                onClick={applyBasicGradient} 
                className="gradient-button"
                title={t('matrice.basicGradient')}
              >
                {t('matrice.basicGradient')}
              </button>
            </div>
          </div>
          <div className="matrice-grid">
            {lettres.map((lettre) => (
              <div
                key={lettre}
                className={`matrice-cell ${isEyedropperActive ? 'eyedropper-cursor' : ''}`}
                style={{
                  backgroundColor: lettresCouleurs[lettre] || '#ffffff',
                  color: getContrastColor(lettresCouleurs[lettre] || '#ffffff'),
                }}
                onClick={() => handleCellClick('lettre', lettre)}
              >
                {lettre}
              </div>
            ))}
          </div>
        </div>

        {caracteresSpeciaux.length > 0 && (
          <div className="matrice-section">
            <h2>{t('matrice.specialCharacters') || 'Caractères spéciaux'}</h2>
            <div className="matrice-grid">
              {caracteresSpeciaux.map((char) => (
                <div
                  key={char}
                  className={`matrice-cell ${isEyedropperActive ? 'eyedropper-cursor' : ''}`}
                  style={{
                    backgroundColor: caracteresSpeciauxCouleurs[char] || '#ffffff',
                    color: getContrastColor(caracteresSpeciauxCouleurs[char] || '#ffffff'),
                  }}
                  onClick={() => handleCellClick('caractere_special', char)}
                >
                  {char}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="matrice-section">
          <h2>{t('matrice.numbers')}</h2>
          <div className="matrice-grid matrice-grid-chiffres">
            {chiffres.map((chiffre) => (
              <div key={chiffre} className="matrice-cell-chiffre-wrapper">
                <div
                  className={`matrice-cell ${isEyedropperActive ? 'eyedropper-cursor' : ''}`}
                  style={{
                    backgroundColor: chiffresCouleurs[chiffre] || '#ffffff',
                    color: getContrastColor(chiffresCouleurs[chiffre] || '#ffffff'),
                  }}
                  onClick={() => handleCellClick('chiffre', chiffre)}
                >
                  {chiffre}
                </div>
                <div
                  className={`matrice-cell-sensation ${chiffresSensations[chiffre] ? 'matrice-cell-sensation-filled' : ''}`}
                  style={chiffresSensations[chiffre] ? {
                    backgroundColor: chiffresCouleurs[chiffre] || '#2563eb',
                    color: getContrastColor(chiffresCouleurs[chiffre] || '#2563eb'),
                    borderColor: 'transparent',
                    '--sensation-color-rgb': hexToRgb(chiffresCouleurs[chiffre] || '#2563eb'),
                  } : {}}
                  onClick={(e) => handleSensationRectangleClick(e, chiffre)}
                  title={chiffresSensations[chiffre] || (t('matrice.sensationDescribe') || 'Décrire la sensation de ce chiffre')}
                />
                <div
                  className={`matrice-cell-symbol ${chiffresSymboles[chiffre] ? 'matrice-cell-symbol-colored' : ''}`}
                  style={getSymbolStyle(chiffresSymboles[chiffre])}
                  onClick={(e) => handleSymbolSquareClick(e, chiffre)}
                  title={t('matrice.symbolSelect') || 'Choisir le symbole'}
                >
                  {getSymbolDisplay(chiffresSymboles[chiffre])}
                </div>
              </div>
            ))}
          </div>
        </div>

        {showSensationModal && sensationModalChiffre !== null && (
          <SensationModal
            chiffre={sensationModalChiffre}
            valeur={chiffresSensations[sensationModalChiffre] || ''}
            onSave={handleSensationSave}
            onClose={() => { setShowSensationModal(false); setSensationModalChiffre(null); }}
            t={t}
            getWordCount={getWordCount}
          />
        )}

        {showSymbolModal && symbolModalChiffre !== null && (
          <div className="symbol-modal-overlay" onClick={() => setShowSymbolModal(false)}>
            <div className="symbol-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{t('matrice.symbolFor') || 'Symbole pour'} {symbolModalChiffre}</h3>
              <div className="symbol-modal-options">
                <button
                  className="symbol-option"
                  onClick={() => handleSymbolSelect('male')}
                  title={t('matrice.symbolMale') || 'Mâle'}
                >
                  <span className="symbol-icon">♂</span>
                  <span>{t('matrice.symbolMale') || 'Mâle'}</span>
                </button>
                <button
                  className="symbol-option"
                  onClick={() => handleSymbolSelect('female')}
                  title={t('matrice.symbolFemale') || 'Femelle'}
                >
                  <span className="symbol-icon">♀</span>
                  <span>{t('matrice.symbolFemale') || 'Femelle'}</span>
                </button>
                <button
                  className="symbol-option"
                  onClick={() => handleSymbolSelect('indeterminate')}
                  title={t('matrice.symbolIndeterminate') || 'Indéterminé'}
                >
                  <span className="symbol-icon">⚧</span>
                  <span>{t('matrice.symbolIndeterminate') || 'Indéterminé'}</span>
                </button>
              </div>
              <button className="symbol-modal-close" onClick={() => setShowSymbolModal(false)}>
                {t('common.cancel') || 'Annuler'}
              </button>
            </div>
          </div>
        )}

        <div className="matrice-section">
          <h2>{t('matrice.days') || 'Jours de la semaine'}</h2>
          <div className="matrice-grid matrice-grid-days">
            {jours.map((_, index) => {
              const key = String(index);
              return (
                <div
                  key={key}
                  className={`matrice-cell matrice-cell-day ${isEyedropperActive ? 'eyedropper-cursor' : ''}`}
                  style={{
                    backgroundColor: joursSemaineCouleurs[key] || '#ffffff',
                    color: getContrastColor(joursSemaineCouleurs[key] || '#ffffff'),
                  }}
                  onClick={() => handleCellClick('jour', key)}
                >
                  {jours[index]}
                </div>
              );
            })}
          </div>
        </div>

        <div className="matrice-section">
          <h2>{t('matrice.months') || 'Mois de l\'année'}</h2>
          <div className="matrice-grid matrice-grid-months">
            {mois.map((_, index) => {
              const key = String(index + 1);
              return (
                <div
                  key={key}
                  className={`matrice-cell matrice-cell-month ${isEyedropperActive ? 'eyedropper-cursor' : ''}`}
                  style={{
                    backgroundColor: moisAnneeCouleurs[key] || '#ffffff',
                    color: getContrastColor(moisAnneeCouleurs[key] || '#ffffff'),
                  }}
                  onClick={() => handleCellClick('mois', key)}
                >
                  {mois[index]}
                </div>
              );
            })}
          </div>
        </div>

        <div className="editor-actions editor-actions-bottom">
          <button onClick={handleSave} disabled={loading} className="save-button">
            {loading ? (id ? t('matrice.updating') || 'Mise à jour...' : 'Enregistrement...') : (id ? t('matrice.update') || 'Mettre à jour' : t('matrice.save'))}
          </button>
          {saved && <span className="saved-message">✓ {id ? (t('matrice.updated') || 'Mis à jour!') : 'Enregistré!'}</span>}
        </div>
      </div>
    </div>
  );
};

// Fonction pour déterminer la couleur du texte selon le fond
function getContrastColor(hexColor) {
  if (!hexColor || hexColor === '#ffffff') return '#000000';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
}

export default MatriceEditor;

